// ts-rest contract for the firm-wide audit-trail VIEWER + EXPORT (PLAT1).
//
// A compliance-owned, filterable who-did-what surface unifying the TWO
// observability pillars (PRD #345 / ADR-0011):
//
//   - PILLAR 1 `mutations` — the durable `audit_events` action trail (every
//     `audit:true` Action + persona_switch + auth.login/logout). Backed by the
//     core `AuditEvent` Object Type.
//   - PILLAR 2 `activity` — the `activity_events` usage/navigation trail
//     (page_view / interaction / api_request / read_access / write_access /
//     auth). Firm-infrastructure, read via a typed repo on the analytics
//     connection.
//
// The two pillars are projected onto ONE displayable row shape so a single
// DataTable + a single tabular export can present them side by side. Filtering
// (employee, department, action/kind, date range, free-text) and pagination are
// resolved SERVER-SIDE — these tables are high-volume.
//
// Endpoints under /api/audit:
//   GET /api/audit/events    — paginated, filtered unified trail
//   GET /api/audit/employees — the staff directory for the employee filter

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Which pillar(s) a query draws from. `all` merges both, time-ordered. */
export const auditPillarSchema = z.enum(['mutations', 'activity', 'all']);
export type AuditPillar = z.infer<typeof auditPillarSchema>;

/**
 * A staff department. Derived SERVER-SIDE from the actor persona on each row
 * via the shared `personaToFrontend` mapping (`im-assistant → desk`), so a row
 * never needs to store its department.
 */
export const auditDepartmentSchema = z.enum(['desk', 'ops', 'comply', 'view']);
export type AuditDepartment = z.infer<typeof auditDepartmentSchema>;

// ---------------------------------------------------------------------------
// Filters (shared by the list endpoint AND the tabular export)
// ---------------------------------------------------------------------------

/**
 * The filter set. `limit`/`offset` are `z.coerce.number` because query-string
 * values arrive as strings. All filters are optional — an unfiltered query
 * returns the newest page across both pillars.
 */
export const auditTrailQuerySchema = z.object({
    /** Pillar selector. Defaults to `all` server-side when omitted. */
    pillar: auditPillarSchema.optional(),
    /** A `users.id` — restrict to one employee's actions across both pillars. */
    employeeUserId: z.string().optional(),
    /** Restrict to actions whose actor persona maps to this department. */
    department: auditDepartmentSchema.optional(),
    /**
     * The specific verb: an `actionKey` for the mutations pillar or an event
     * `kind` for the activity pillar. Matched against the unified `action`
     * column, so it works regardless of pillar.
     */
    action: z.string().optional(),
    /** Inclusive lower bound (ISO date/time) on the event time. */
    dateFrom: z.string().optional(),
    /** Inclusive upper bound (ISO date/time) on the event time. */
    dateTo: z.string().optional(),
    /** Free-text needle matched against summary / action / target / path. */
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(500).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
export type AuditTrailQuery = z.infer<typeof auditTrailQuerySchema>;

// ---------------------------------------------------------------------------
// Row + response
// ---------------------------------------------------------------------------

/**
 * One displayable audit-trail row — the projection BOTH pillars are mapped
 * onto. `pillar` says which table it came from; `action` is the specific verb
 * (actionKey for mutations, event kind for activity). `actorName`/`actorEmail`
 * are joined from the `users` directory at read-time (never denormalised).
 */
export const auditTrailRowSchema = z.object({
    id: z.string(),
    /** Event time (ISO 8601). `occurred_at` for mutations, `received_at` for activity. */
    occurredAt: z.string(),
    pillar: z.enum(['mutations', 'activity']),
    actorId: z.string().nullable(),
    actorName: z.string().nullable(),
    actorEmail: z.string().nullable(),
    /**
     * The IM that an executive assistant acted for, from `acting_for_user_id`
     * of a mutation row. Null for every other actor and for an activity row.
     */
    actingForUserId: z.string().nullable().optional(),
    /** The display name of that IM, from the `users` directory at read time. */
    actingForName: z.string().nullable().optional(),
    department: auditDepartmentSchema.nullable(),
    persona: z.string().nullable(),
    /** Coarse discriminator: `'mutation'` or the activity kind (page_view, …). */
    kind: z.string(),
    /** Specific verb: actionKey (mutations) or the activity kind (activity). */
    action: z.string(),
    targetType: z.string().nullable(),
    targetId: z.string().nullable(),
    path: z.string().nullable(),
    summary: z.string().nullable(),
});
export type AuditTrailRow = z.infer<typeof auditTrailRowSchema>;

export const auditTrailResponseSchema = z.object({
    rows: z.array(auditTrailRowSchema),
    /** Total matching rows across both pillars (for pagination chrome). */
    total: z.number().int().nonnegative(),
});
export type AuditTrailResponse = z.infer<typeof auditTrailResponseSchema>;

/** A staff member for the employee filter dropdown. */
export const auditEmployeeSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string(),
});
export type AuditEmployee = z.infer<typeof auditEmployeeSchema>;

export const auditEmployeesResponseSchema = z.object({
    employees: z.array(auditEmployeeSchema),
});
export type AuditEmployeesResponse = z.infer<typeof auditEmployeesResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const auditContract = c.router(
    {
        listEvents: {
            method: 'GET',
            path: '/audit/events',
            query: auditTrailQuerySchema,
            responses: {
                200: auditTrailResponseSchema,
            },
            summary: 'Paginated, filtered firm-wide audit trail across both observability pillars',
        },
        listEmployees: {
            method: 'GET',
            path: '/audit/employees',
            responses: {
                200: auditEmployeesResponseSchema,
            },
            summary: 'The staff directory backing the employee filter dropdown',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
